import { Injectable } from '@angular/core';
import { PlatformLocation } from '@angular/common';
import { RendererFactory2, Renderer2 } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Observable, Subscription, of } from 'rxjs';
import { switchMap, take, tap } from 'rxjs/operators';
import { ResultpageService } from './resultpage.service';
import { LoginService } from './login.service';
import { OfferDataUtilsService } from './offer-data-utils.service';
import { CacheService, CacheEntry } from './cache.service';
import {
  Filter,
  FilterOptions,
  Loan,
  OffersOfApplication,
  Result,
} from '../type/result_type';

declare const window: any;

@Injectable()
export abstract class EternalPageService {
  protected eternalCache = new Map<string, CacheEntry<Result>>();
  // Data properties
  public dataLoan!: Loan;
  public dataFilter!: Filter;
  public dataFilterOptions!: FilterOptions;
  public dataOffers!: OffersOfApplication[];
  public dataNoOffers!: OffersOfApplication[];

  // Subscriptions
  protected subGet!: Subscription;
  protected subFilterPost!: Subscription;
  protected filterPostGet!: Subscription;

  // State flags
  public showDefaultPlaceholders: boolean = true;
  public loading: boolean = true;
  public filterOverlay: boolean = false;
  public showDataOffers: boolean = true;
  public eternalPage: boolean = true;

  // Paths
  public pathname!: string;
  public basePath!: string;

  protected postFilterObserv$!: Observable<any>;
  protected renderer: Renderer2;

  constructor(
    protected route: ActivatedRoute,
    protected resultpageService: ResultpageService,
    protected location: PlatformLocation,
    protected rendererFactory: RendererFactory2,
    protected loginService: LoginService,
    protected offerDataUtils: OfferDataUtilsService,
    protected cacheService: CacheService
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  initComponent(): void {
    this.pathname = this.location.pathname;

    this.loginService.checkLogin().subscribe({
      next: () => this.getData(),
      error: () => (this.loading = false),
    });

    window['dataLayer'] = window['dataLayer'] || [];
    const deviceOutput = window['wf_deviceoutput'];
    window['dataLayer'].push({
      ...window['dataLayer'][0],
      event: this.getGtmEventName(),
      deviceoutput: deviceOutput,
    });
  }

  ngOnDestroy(): void {
    if (this.subGet) this.subGet.unsubscribe();
    if (this.subFilterPost) this.subFilterPost.unsubscribe();
    if (this.filterPostGet) this.filterPostGet.unsubscribe();
  }

  protected getCacheKey(): string {
    const tab = this.offerDataUtils.getKreditcenterTab();
    return `eternal:${tab}`;
  }

  protected invalidateCache(): void {
    this.cacheService.clear(this.eternalCache);
  }

  private getData(): void {
    const cacheKey = this.getCacheKey();

    // Return cached data if valid, avoiding redundant API call
    const dataSource = this.cacheService.isValid(this.eternalCache, cacheKey)
      ? of(this.cacheService.get(this.eternalCache, cacheKey)!)
      : this.getApiData().pipe(
          tap((data: Result) => {
            this.cacheService.set(this.eternalCache, cacheKey, data);
          })
        );

    this.subGet = dataSource
      .pipe(take(1))
      .subscribe((data: Result) => {
        this.processData(data);
        this.offerDataUtils.updateEternalOffersData(data);
      });
  }

  private processData(data: Result): void {
    if (!data || Object.keys(data).length === 0) return;

    this.dataFilter = data.filter;
    this.dataOffers = data.offersOfApplication;
    this.dataNoOffers = this.offerDataUtils.sortNoOffersData(
      data.noOffersOfApplication
    );

    if (this.dataOffers?.length === 0) {
      this.showDataOffers = false;
    }

    this.resultpageService.updatedFilerDataPost(data.filter);
    this.resultpageService.updateFilerOptionsData(data.filterOptions);
    this.resultpageService.updatedStateFilerData(1);
    this.resultpageService.updateOffersData(this.dataOffers);
    this.resultpageService.updateSmilyContext(data.context?.smily);

    if (this.dataOffers?.length >= 3) {
      this.showDefaultPlaceholders = false;
    }

    this.loading = false;
  }

  postFilter(): void {
    this.loading = true;
    let obj: any = {};

    this.filterPostGet = this.resultpageService.filterDataPostObserv$.subscribe(
      (data) => {
        obj = { ...data };
        if (obj.newComparison) {
          delete obj.newComparison;
        }
      }
    );

    this.postFilterObserv$ = this.route.paramMap.pipe(
      switchMap(() => this.postApiFilter(obj))
    );

    this.subFilterPost = this.postFilterObserv$.pipe(
      tap((data: Result) => {
        const cacheKey = this.getCacheKey();
        this.cacheService.set(this.eternalCache, cacheKey, data);
      })
    ).subscribe((data: Result) => {
      this.dataFilter = data.filter;
      this.dataFilterOptions = data.filterOptions;
      this.dataOffers = data.offersOfApplication;
      this.dataNoOffers = this.offerDataUtils.sortNoOffersData(
        data.noOffersOfApplication
      );

      this.resultpageService.updateFilerOptionsData(data.filterOptions);
      this.resultpageService.updateOffersData(this.dataOffers);
      this.resultpageService.updateSmilyContext(data.context?.smily);
      this.offerDataUtils.updateEternalOffersData(data);

      if (this.dataOffers?.length >= 3) {
        this.showDefaultPlaceholders = false;
      }

      this.loading = false;
      this.filterOverlay = false;
      this.resultpageService.updatedOverlay(this.filterOverlay);
    });
  }

  getSingleOffer(offerId: string): OffersOfApplication | undefined {
    return this.offerDataUtils.getSingleOfferById(this.dataOffers, offerId);
  }

  get eternalOffersDataObserv$() {
    return this.offerDataUtils.eternalOffersDataObserv$;
  }

  // Abstract methods to be implemented by concrete services
  protected abstract getApiData(): Observable<Result>;
  protected abstract postApiFilter(obj: any): Observable<Result>;
  protected abstract getGtmEventName(): string;
}

